'use client';

import { createLogger } from '@svenskaspelab/nova-core/log';
import { trackEcommerce, trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import { getAccountBalance, loginRedirect, useAccountBalance, useAuth } from '@svenskaspelab/nova-core/nextjs/auth';
import { useMFE } from '@svenskaspelab/nova-core/nextjs/mfe';
import { version as coreVersion } from '@svenskaspelab/nova-core/version';
import {
  getMarketplaceBalance,
  getRetailerBalance,
} from '@svenskaspelab/poolbetting/core/frameworks/nextjs/marketplace';
import type { DrawNumber } from '@svenskaspelab/poolbetting/schema/draw';
import type { Commission } from '@svenskaspelab/poolbetting/schema/input';
import type { ActivatedDrawId } from '@svenskaspelab/poolbetting/schema/marketplace';
import { isCouponProduct, type Product, PRODUCT_MAP_VARIANTS } from '@svenskaspelab/poolbetting/schema/product';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type ComponentType, type ReactNode, useCallback, useContext, useMemo } from 'react';

import { walletCachedContext } from '@/components/index.browser';

import ConsumePixError from './consume-pix-error';
import ConsumePixFallback from './consume-pix-fallback';

const logger = createLogger('quick-pick');

/**
 * Component for färdiga-spel PIX.
 *
 * @param root props
 * @param root.commission optional commission for the PIX transaction
 * @param root.drawNumber open draw number
 * @param root.product current product
 * @param root.activatedDrawId optional activated draw id for marketplace wallet
 * @param root.role the role of the user
 * @returns The consumed PIX component
 */
export default function ConsumePixComponent({
  commission,
  drawNumber,
  product,
  activatedDrawId,
  role,
}: {
  commission?: Commission;
  drawNumber: DrawNumber;
  product: Product;
  activatedDrawId?: ActivatedDrawId;
  role: string;
}): ReactNode {
  const productId = PRODUCT_MAP_VARIANTS[product];
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { forceRefresh } = useAccountBalance();
  const { context } = walletCachedContext;
  const walletContext = useContext(context);
  const activatedDraws =
    walletContext?.state && 'activatedDraws' in walletContext.state ? walletContext.state.activatedDraws : undefined;

  const activatedRetailDraw =
    walletContext?.state && 'activatedDraw' in walletContext.state ? walletContext.state.activatedDraw : undefined;

  const commissionPercent = activatedRetailDraw ? (commission ?? activatedRetailDraw.commissionPercent) : undefined;

  const marketplaceWallets = useMemo(() => {
    const record: Record<number, { balance: number; soldShares: number; sharePrice: number }> = {};

    if (activatedDraws) {
      for (const draw of activatedDraws) {
        const sharePrice = Number.parseInt(draw.SharePrice, 10);
        const soldShares = sharePrice > 0 ? draw.balance / sharePrice : 0;
        record[draw.activatedDrawId] = { balance: draw.balance, soldShares, sharePrice };
      }
      return record;
    }

    if (activatedRetailDraw) {
      record[activatedRetailDraw.activatedDrawId] = {
        balance: Number.parseInt(activatedRetailDraw.currentPlayableAmount, 10),
        soldShares: activatedRetailDraw.soldShares,
        sharePrice: Number.parseInt(activatedRetailDraw.sharePrice, 10),
      };
      return record;
    }

    return undefined;
  }, [activatedDraws, activatedRetailDraw]);

  const removeActivatedDrawIdFromUrl = useCallback(() => {
    const qs = new URLSearchParams(searchParams.toString());
    if (qs.has('activatedDrawId')) {
      qs.delete('activatedDrawId');
      const query = qs.toString();
      router.replace(`${pathname}${query ? `?${query}` : ''}`, { scroll: false });
    }
  }, [router, pathname, searchParams]);

  const refreshWalletData = useCallback(async () => {
    if (role !== 'Player' && role !== 'Retailer') {
      return;
    }
    forceRefresh();
    if (isCouponProduct(product)) {
      if (role === 'Player') {
        const result = await getMarketplaceBalance({ product, drawNumber });
        if (result.status === 'SUCCESS' && result.data.activatedDraws.length > 0) {
          walletContext?.setState(result.data);
        } else {
          walletContext?.setState(undefined);
        }
      } else if (activatedDrawId) {
        const result = await getRetailerBalance({ activatedDrawId });
        if (result.status === 'SUCCESS' && result.data.activatedDraw) {
          walletContext?.setState(result.data);
        } else {
          walletContext?.setState(undefined);
        }
      }
    }
  }, [activatedDrawId, role, forceRefresh, product, drawNumber, walletContext]);

  type PixComponentProps = {
    drawNumber: number;
    productId: number;
    activatedDrawId?: number;
    commissionPercent?: Commission;
    marketplaceWallets?: Record<number, { balance: number; soldShares: number; sharePrice: number }>;
    onPaymentSuccess?: () => void;
    onRefreshWalletData?: () => void;
  };

  const {
    Component: PixComponent,
    error,
    isLoading,
  } = useMFE<PixComponentProps>({
    name: 'pix',
    component: 'SharedPixComponent',
    shared: {
      '@svenskaspelab/nova-core/nextjs/auth': {
        version: coreVersion,
        lib: () => ({ getAccountBalance, loginRedirect, useAuth }),
      },
      '@svs/nova-core/nextjs/analytics': {
        version: coreVersion,
        lib: () => ({ trackEvent, trackEcommerce }),
      },
    },
  }) as {
    Component: ComponentType<PixComponentProps> | null;
    error: Error | null;
    isLoading: boolean;
  };

  logger.debug('Rendering ConsumePixComponent', { drawNumber, productId, activatedDrawId, isLoading, error });
  if (isLoading) {
    return <ConsumePixFallback />;
  }

  if (error) {
    return <ConsumePixError />;
  }

  return (
    (PixComponent && (
      <PixComponent
        drawNumber={drawNumber}
        productId={productId}
        activatedDrawId={activatedDrawId}
        commissionPercent={commissionPercent}
        marketplaceWallets={marketplaceWallets}
        onPaymentSuccess={() => {
          void refreshWalletData().finally(removeActivatedDrawIdFromUrl);
        }}
        onRefreshWalletData={() => {
          void refreshWalletData();
        }}
      />
    )) ||
    undefined
  );
}
